import type { SentryLike } from './sentryLike';
import { registerSentrySdk } from './registry';
import { REPLAY_MASK_SELECTORS } from './replayMasking';
import {
	getTracesSampleRate,
	REPLAY_ON_ERROR_SAMPLE_RATE,
	REPLAY_SESSION_SAMPLE_RATE,
} from './constants';

export interface BrowserSentryOptions {
	dsn: string;
	environment: string;
	/** Service name shown in Sentry tags — use SentryServiceTag from networked-shared-interfaces/constants/observability-service-tag */
	serviceTag: string;
	release?: string;
	tracePropagationTargets?: (string | RegExp)[];
	/** Verbose Sentry console logging (requires `removeDebugLogging: false` in `withSentryConfig`). */
	debug?: boolean;
}

/**
 * Registers the Sentry SDK with the shared registry AND initialises it.
 * Pass the host app's Sentry package as the first argument so this file
 * never imports @sentry/* directly.
 *
 * @example
 *   import * as Sentry from '@sentry/react';
 *   initBrowserSentry(Sentry, { dsn, environment });
 */
export function initBrowserSentry(sdk: SentryLike, options: BrowserSentryOptions): void {
	if (!options.dsn) return;

	registerSentrySdk(sdk, options.serviceTag);

	const initOptions: Record<string, unknown> = {
		dsn: options.dsn,
		environment: options.environment,
		integrations: [
			sdk.browserTracingIntegration(),
			sdk.replayIntegration({
				// Hide by default (decision D9, 2026-10-28): all text and all media are masked, so a new
				// screen can never leak members' words or photos. Sentry replays are for error context;
				// the layout and the click trail stay. REPLAY_MASK_SELECTORS stays as a second layer.
				maskAllText: true,
				blockAllMedia: true,
				maskAllInputs: true,
				mask: REPLAY_MASK_SELECTORS,
			}),
		],

		tracesSampleRate: getTracesSampleRate(options.environment),

		replaysSessionSampleRate: REPLAY_SESSION_SAMPLE_RATE,
		replaysOnErrorSampleRate: REPLAY_ON_ERROR_SAMPLE_RATE,

		tracePropagationTargets: options.tracePropagationTargets ?? ['localhost'],
	};

	if (options.release) {
		initOptions.release = options.release;
	}

	if (options.debug) {
		initOptions.debug = true;
	}

	sdk.init(initOptions);
}
